import { ROLE, type Role } from '../contexts/CustomerHubContext'

import type { EmptyStateProps } from './EmptyState'
import type { TranslationFunction } from '@susu/i18n'

interface SizePassportEmptyStateOptions {
  role?: Role
  onBookAppointment: () => void
  onLearnMore: () => void
  t: TranslationFunction
}

/**
 * Builds the EmptyState props for the Size Passport tab.
 *
 * The in-store (style-advisor) view differs from the online (customer) view:
 * it shows a heading + body with no imagery and no inline CTAs — the sticky
 * "Select customer" footer is owned by the consuming app
 * (rp-active-customers-list). With no image stack there is no top margin, so
 * `pt-40` adds breathing room below the tab bar to match the design.
 *
 * Extracted from CustomerHubCore so the role branching can be unit-tested.
 */
export function getSizePassportEmptyStateProps({
  role,
  onBookAppointment,
  onLearnMore,
  t,
}: SizePassportEmptyStateOptions): EmptyStateProps {
  if (role === ROLE.STYLE_ADVISOR) {
    return {
      testId: 'size-passport-empty-state',
      className: 'pt-40',
      images: [],
      title: (
        <span className="font-medium">
          {t('sizePassport.emptyStyleAdvisorTitle')}
        </span>
      ),
      description: t('sizePassport.emptyStyleAdvisorDescription'),
    }
  }

  return {
    testId: 'size-passport-empty-state',
    title: t('sizePassport.emptyCustomer'),
    primaryAction: {
      label: t('sizePassport.bookAppointment'),
      onClick: onBookAppointment,
      variant: 'filled',
      testId: 'size-passport-book-appointment-button',
    },
    secondaryAction: {
      label: t('sizePassport.learnMore'),
      onClick: onLearnMore,
      testId: 'size-passport-learn-more-button',
    },
  }
}
